/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2022, 2023
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { createAction, createAsyncThunk } from '@reduxjs/toolkit';

import { SkillReference } from '../../../../ts/assistant';
import { ReactivateSession } from '../../../../ts/session';
import { Skill, SkillReferenceType } from '../../../../ts/skill';
import {
  SnapshotSkill,
  SnapshotSkillId,
  SnapshotsSkill,
  SnapshotStatus
} from '../../../../ts/snapshot';
import { AsyncAppThunk, CreateAsyncThunk } from '../../../../ts/state';
import * as API from '../../api/snapshots';
import { triggerSaveJson } from '../../util/download';
import { getErrorString } from '../../util/errorHandler';
import {
  createdObjectEvents,
  deletedObjectEvents
} from '../../util/trackers/segment';
import { selectIsAccessUpdatesEnabled } from '../features/selectors';
import { isCacheExpired } from '../helper';
import { selectIsNewIa } from '../instance/selectors';
import { addErrorMessage, addMessage, ToastStatus } from '../messages/actions';
import { getAssistantId } from '../session/selectors';
import { isProcessing, sleep } from '../skills/helper';
import {
  selectIsSkillSnapshotsLoading,
  selectSkillSnapshotById,
  selectSnapshotFetchedBySkillId
} from './selectors';

export const notifySnapshotStatus = createAction<SnapshotSkill>(
  'snapshots/create/status'
);
export const notifySnapshotResult = createAction<SnapshotSkill>(
  'snapshots/create/result'
);

export const setClassicSnapshotSkillId = createAction<string>(
  'snapshots/setClassicSnapshotSkillId'
);

export const listSnapshots = skillId => listSnapshotsWithArgs({ skillId });

interface ListSnapshotsArgs {
  skillId: string;
  reactivateSession?: ReactivateSession;
}

export const listSnapshotsWithArgs = createAsyncThunk<
  SnapshotsSkill,
  ListSnapshotsArgs,
  CreateAsyncThunk
>(
  'snapshots/list',
  async ({ skillId, reactivateSession }, { dispatch, getState }) => {
    try {
      // Use revised API for Access Control improvements if enabled & isNewIA
      // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with simpler isNewIA on call to listSnapshots
      const assistantId = getAssistantId(getState());
      const isNewIA = selectIsNewIa(getState());
      const useNewIAUpdatedAccess =
        isNewIA && selectIsAccessUpdatesEnabled(getState());
      dispatch(setClassicSnapshotSkillId(skillId));
      const response = await API.listSnapshots(
        skillId,
        useNewIAUpdatedAccess,
        assistantId,
        reactivateSession
      );

      response?.snapshots?.forEach(snapshot => {
        if (isProcessing(snapshot)) {
          dispatch(checkSnapshotProgress({ skillId, snapshot }));
        }
      });

      const { snapshots } = response;
      return { skillId, snapshots };
    } catch (error) {
      dispatch(addErrorMessage(error));
      throw error;
    }
  },
  {
    condition: ({ skillId }, { getState }) => {
      return !selectIsSkillSnapshotsLoading(getState(), skillId);
    }
  }
);

export const listSnapshotsIfRequired =
  ({
    skillId,
    reactivateSession
  }: ListSnapshotsArgs): AsyncAppThunk<Promise<SnapshotsSkill>> =>
  async (dispatch, getState) => {
    dispatch(setClassicSnapshotSkillId(skillId));

    const listFetched = selectSnapshotFetchedBySkillId(getState(), skillId);
    if (!isCacheExpired(listFetched)) {
      const skillSnapshot = selectSkillSnapshotById(getState(), skillId);
      return Promise.resolve(skillSnapshot);
    }
    await dispatch(listSnapshotsWithArgs({ skillId, reactivateSession }));
    return selectSkillSnapshotById(getState(), skillId);
  };

export const checkSnapshotProgress =
  (params: SnapshotSkill): AsyncAppThunk<Promise<SnapshotSkill>> =>
  async dispatch => {
    let waitTimeInMs = 15000; // 15 seconds
    let snapshot = params.snapshot;
    const skillId = params.skillId;

    // Wait until the snapshot is ready and dispatch updates in the meantime
    while (isProcessing(snapshot)) {
      try {
        dispatch(notifySnapshotStatus({ skillId, snapshot }));
        await sleep(waitTimeInMs);
        snapshot = await API.getSnapshot(skillId, snapshot.snapshot);
      } catch (error) {
        // Wait a bit more if there is an error
        waitTimeInMs = 120000;
      }
    }

    // Notify the user if the snapshot failed
    if (snapshot.status === SnapshotStatus.FAILED) {
      await dispatch(
        addMessage({
          message: 'Error creating the snapshot',
          status: ToastStatus.ERROR
        })
      );
    }

    // Notify the store
    dispatch(notifySnapshotResult({ skillId, snapshot }));
    return { skillId, snapshot };
  };

type CreateSnapshot = {
  skillId: string;
  description: string;
  async?: boolean;
};

export const createSnapshot = createAsyncThunk<
  SnapshotSkill,
  CreateSnapshot,
  CreateAsyncThunk
>(
  'snapshots/create',
  async ({ skillId, description, async = true }, { dispatch }) => {
    try {
      const snapshot = await API.createSnapshot(skillId, description, async);
      if (isProcessing(snapshot)) {
        dispatch(checkSnapshotProgress({ skillId, snapshot }));
      }

      createdObjectEvents.snapshot({
        object: snapshot.snapshot,
        resultValue: 'success',
        successFlag: true
      });

      return { skillId, snapshot };
    } catch (error) {
      createdObjectEvents.snapshot({
        object: 'snapshot',
        resultValue: getErrorString(error),
        successFlag: false
      });

      dispatch(addErrorMessage(error));
      throw error;
    }
  }
);

export const deleteSnapshot = createAsyncThunk<
  SnapshotSkillId,
  SnapshotSkillId,
  CreateAsyncThunk
>('snapshots/delete', async ({ skillId, snapshotId }, { dispatch }) => {
  try {
    await API.deleteSnapshot(skillId, snapshotId);

    deletedObjectEvents.snapshot({
      object: skillId ? `${skillId}--#${snapshotId}` : snapshotId,
      resultValue: 'success',
      successFlag: true
    });

    return { skillId, snapshotId };
  } catch (error) {
    deletedObjectEvents.snapshot({
      object: skillId ? `${skillId}--#${snapshotId}` : snapshotId,
      resultValue: getErrorString(error),
      successFlag: false
    });
    dispatch(addErrorMessage(error));
    throw error;
  }
});

export const revertSnapshot = createAsyncThunk<
  Skill,
  SnapshotSkillId,
  CreateAsyncThunk
>(
  'snapshots/revert',
  async ({ skillId, snapshotId }, { dispatch, getState }) => {
    try {
      // Use revised API for Access Control improvements if enabled & isNewIA
      // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess
      //   with simpler isNewIA on call to revertSnapshot
      const assistantId = getAssistantId(getState());
      const isNewIA = selectIsNewIa(getState());
      const useNewIAUpdatedAccess =
        isNewIA && selectIsAccessUpdatesEnabled(getState());
      const skill = await API.revertSnapshot(
        skillId,
        useNewIAUpdatedAccess,
        assistantId,
        snapshotId
      );
      return skill;
    } catch (error) {
      dispatch(addErrorMessage(error));
      throw error;
    }
  }
);

export const assignSnapshot = createAsyncThunk<
  SkillReference,
  SnapshotSkillId & { assistantId: string },
  CreateAsyncThunk
>(
  'snapshots/assign',
  async ({ assistantId, skillId, snapshotId }, { dispatch }) => {
    try {
      const skillReference = await API.assignSnapshotToAssistant(
        assistantId,
        skillId,
        snapshotId
      );

      return skillReference;
    } catch (error) {
      dispatch(addErrorMessage(error));
      throw error;
    }
  }
);

/**
 * Swaps the snapshot for a given Assistant. Only used in classic IA. New IA uses
 * the releases API.
 */
export const swapSnapshot = createAsyncThunk<
  SkillReference,
  SnapshotSkillId & { assistantId: string; skillReference: SkillReferenceType },
  CreateAsyncThunk
>(
  'snapshots/swap',
  async ({ assistantId, skillReference, snapshotId }, { dispatch }) => {
    try {
      const assistant = await API.updateSnapshotInAssistant(
        assistantId,
        skillReference,
        snapshotId
      );

      return assistant;
    } catch (error) {
      dispatch(addErrorMessage(error));
      throw error;
    }
  }
);

/**
 * Expose the inner logic of `exportSnapshot()` so it can be called and
 * explicitly waited in order to serialize multi-file saves (Safari bug 52780)
 */
export const exportSnapshotInner = async (
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string,
  snapshotId: string,
  name?: string,
  releaseId?: string
): Promise<Skill> => {
  const data = await API.exportSnapshot(
    skillId,
    useNewIAUpdatedAccess,
    assistantId,
    snapshotId
  );

  //the v2 API to download a snapshot which is not quite a skill nor a workspace
  //for now we need to shape the data into a workspace as upload only supports workspaces
  //skill name is required as that is not stored in the snapshot metadata
  if (data) {
    data.name = name || data.name || '';
    const cleanName = data.name.replace(/\s/g, '-').toLowerCase();
    const fileName = `${cleanName}-v${releaseId || snapshotId}.json`;
    triggerSaveJson(data, fileName);
  }

  return data;
};

export const exportSnapshot = createAsyncThunk<
  Skill,
  SnapshotSkillId,
  CreateAsyncThunk
>(
  'snapshots/export',
  async ({ skillId, snapshotId }, { dispatch, getState }) => {
    try {
      // Use revised API for Access Control improvements if enabled & isNewIA
      // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with simpler isNewIA on call
      const assistantId = getAssistantId(getState());
      const isNewIA = selectIsNewIa(getState());
      const useNewIAUpdatedAccess =
        isNewIA && selectIsAccessUpdatesEnabled(getState());
      const response = await exportSnapshotInner(
        skillId,
        useNewIAUpdatedAccess,
        assistantId,
        snapshotId
      );
      return response;
    } catch (error) {
      dispatch(addErrorMessage(error));
      throw error;
    }
  }
);
